import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  QueryOperator,
  FileQueryOperators,
  FILE_QUERY_OPERATORS,
  VqlQuery,
} from "types";

import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import QueryModal from "views/components/QueryModal";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import useVqlParams from "hooks/useVqlParams";
import FolderIcon from "assets/icons/FolderIcon";
import styles from "./style.module.scss";
import {
  createAndUpdateFolderFilter,
  isVqlQuery,
  getQueryValuesForFilter,
} from "helpers/utility/vqlHelpers";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { useTextOrMultiQueryModal } from "hooks/UseQueryModal/useTextOrMultiQueryModal";
import { useTreeFolderData } from "../FileQueryModal/useTreeFolderData";

interface FolderQueryModalProps {
  editQueryId?: string;
}

export default function FolderQueryModal({
  editQueryId,
}: FolderQueryModalProps) {
  const {
    addVqlQuery,
    findVqlQuery,
    updateVqlQuery,
    removeVqlQuery,
    vqlQueries,
  } = useVqlParams();
  const { folders: foldersFromTreeFolder, isLoading } = useTreeFolderData();

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "folder") : null;
  const previousOperator = oldQuery
    ? convertAPIOperator(oldQuery?.folder.op)
    : QueryOperator.IS_ONE_OF;
  const [selectedOperator, setSelectedOperator] = useState(previousOperator);

  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "folder") &&
          convertAPIOperator(query.folder?.op) === selectedOperator,
      ) || null
    );
  }, [vqlQueries, selectedOperator]);

  const queryValues = useMemo(() => {
    return getQueryValuesForFilter(
      editQueryId,
      oldQuery,
      selectedOperator,
      previousOperator,
      existingQuery,
      "folder",
    );
  }, [
    editQueryId,
    oldQuery,
    selectedOperator,
    previousOperator,
    existingQuery,
  ]);

  const previousValues = useMemo(
    () =>
      queryValues && queryValues.length > 0
        ? foldersFromTreeFolder.map((folder) => {
            const isSelected = Array.isArray(queryValues)
              ? queryValues.includes(folder.full_path)
              : queryValues === folder.full_path;
            return { ...folder, selected: isSelected };
          })
        : foldersFromTreeFolder,
    [foldersFromTreeFolder, queryValues],
  );

  const previousSearchText = queryValues
    ? Array.isArray(queryValues)
      ? ""
      : (queryValues as string)
    : "";

  const [folders, setFolders] = useState(previousValues);
  const [searchText, setSearchText] = useState(previousSearchText);
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    setFolders(previousValues);
    setSearchText(previousSearchText);
    // eslint-disable-next-line
  }, [existingQuery?.id, isLoading]);

  const selectedMode =
    selectedOperator === QueryOperator.IS_ONE_OF ? "multiple" : "text";

  const selectedFolders = useMemo(
    () => folders.filter((folder) => folder.selected),
    [folders],
  );

  const { isSubmitDisabled } = useTextOrMultiQueryModal({
    mode: selectedMode,
    selectedItems: selectedFolders.map((f) => f.id),
    previousItems: previousValues.filter((f) => f.selected).map((f) => f.id),
    searchText,
    previousText: previousSearchText,
    selectedOperator,
    previousOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const placeholder = selectedMode === "multiple" ? "Search..." : "Text Input";

  const handleRemoveFolder = (id: number | string) => {
    const newFolders = folders.map((folder) => {
      if (folder.id === id) {
        return { ...folder, selected: false };
      }

      return folder;
    });
    setFolders(newFolders);
  };

  const handleSelectFolder = (id: number | string) => {
    const newFolders = folders.map((folder) => {
      if (folder.id === id) {
        return { ...folder, selected: !folder.selected };
      }
      return folder;
    });
    setFolders(newFolders);
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    setSelectedOperator(operator);
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    const values =
      selectedMode === "multiple"
        ? selectedFolders.map((folder) => folder.full_path)
        : searchText;

    if (editQueryId && values.length === 0) {
      removeVqlQuery(editQueryId);
      dispatch(setSelectedQueryModal(null));
      return;
    }

    if (existingQuery && values.length === 0) {
      removeVqlQuery(existingQuery.id);
      dispatch(setSelectedQueryModal(null));
      return;
    }

    const vqlQuery = createAndUpdateFolderFilter(
      values,
      selectedOperator as FileQueryOperators,
      editQueryId || existingQuery?.id,
    );

    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={<FolderIcon />}>Folder</QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...FILE_QUERY_OPERATORS]}
          selectedOperator={selectedOperator}
          onSelect={handleOptionSelection}
        />
        <QueryModalInput
          options={folders.map((folder) => ({
            id: folder.id,
            text: folder.full_path,
          }))}
          mode={selectedMode}
          searchText={searchText}
          placeholder={placeholder}
          selected={selectedFolders.map((folder) => folder.id)}
          onRemove={handleRemoveFolder}
          onSelect={handleSelectFolder}
          onInputChange={handleInputChange}
          isLoading={isLoading}
        />

        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
